import React, { useState } from 'react';
import { useComunicacao } from '../../contexts/ComunicacaoContext';
import { useAuth } from '../../contexts/AuthContext';
import ModalCriarTopico from './ModalCriarTopico';
import ModalEditarTopico from './ModalEditarTopico';
import ConfirmDialog from '../common/ConfirmDialog';
import ModalMembrosCanal from './ModalMembrosCanal';
import ModalEditarCanal from './ModalEditarCanal';
import { format } from 'date-fns';

const AreaTopicos: React.FC = () => {
  const { canalSelecionado, topicos, selecionarTopico, removerTopico } = useComunicacao();
  const { usuario } = useAuth();
  const [modalCriarAberto, setModalCriarAberto] = useState(false);
  const [modalEditarAberto, setModalEditarAberto] = useState(false);
  const [topicoParaEditar, setTopicoParaEditar] = useState<string | null>(null);
  const [modalConfirmacaoAberto, setModalConfirmacaoAberto] = useState(false);
  const [topicoParaRemover, setTopicoParaRemover] = useState<string | null>(null);
  const [modalMembrosAberto, setModalMembrosAberto] = useState(false);
  const [modalGerenciarMembrosAberto, setModalGerenciarMembrosAberto] = useState(false);
  const [termoBusca, setTermoBusca] = useState('');

  const isAdmin = usuario?.tipo === 'admin';
  const podeCriarTopico = canalSelecionado?.tipo === 'geral' ? isAdmin : true;

  const REGEX_DIACRITICOS = new RegExp('[̀-ͯ]', 'g');
  const normalizar = (texto: string) =>
    texto.toLowerCase().normalize('NFD').replace(REGEX_DIACRITICOS, '');

  const termoNormalizado = normalizar(termoBusca.trim());
  const topicosFiltrados = termoNormalizado
    ? topicos.filter((topico) =>
        normalizar(topico.titulo || '').includes(termoNormalizado) ||
        normalizar(topico.mensagem || '').includes(termoNormalizado) ||
        normalizar(topico.autor_nome || '').includes(termoNormalizado)
      )
    : topicos;

  const handleEditarTopico = (topicoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setTopicoParaEditar(topicoId);
    setModalEditarAberto(true);
  };

  const handleRemoverTopico = (topicoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setTopicoParaRemover(topicoId);
    setModalConfirmacaoAberto(true);
  };

  const confirmarRemocao = async () => {
    if (topicoParaRemover) {
      try {
        await removerTopico(topicoParaRemover);
        setModalConfirmacaoAberto(false);
        setTopicoParaRemover(null);
      } catch (error) {
        console.error('Erro ao remover tópico:', error);
      }
    }
  };

  if (!canalSelecionado) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white">
        <div className="text-center text-gray-500">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p className="text-lg font-medium">Selecione um canal</p>
          <p className="text-sm mt-2">Escolha um canal na barra lateral para ver os tópicos</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full bg-white">
        <div className="border-b border-gray-200 p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800">{canalSelecionado.nome}</h2>
              {canalSelecionado.tipo === 'departamento' && (
                <button
                  onClick={() => setModalMembrosAberto(true)}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-md transition-colors flex items-center gap-1"
                  title="Ver membros do departamento"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Membros
                </button>
              )}
              {canalSelecionado.tipo === 'personalizado' && (isAdmin || canalSelecionado.criadorId === usuario?.id || canalSelecionado.criador_id === usuario?.id) && (
                <button
                  onClick={() => setModalGerenciarMembrosAberto(true)}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-md transition-colors flex items-center gap-1"
                  title="Gerenciar membros do canal"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Gerenciar Membros
                </button>
              )}
            </div>
            {canalSelecionado.descricao && (
              <p className="text-sm text-gray-600 mt-1">{canalSelecionado.descricao}</p>
            )}
          </div>
          {podeCriarTopico && (
            <button
              onClick={() => setModalCriarAberto(true)}
              className="px-4 py-2 bg-bravo-primary hover:bg-blue-600 text-white rounded-md transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Criar Tópico
            </button>
          )}
        </div>

        {topicos.length > 0 && (
          <div className="px-4 pt-3 pb-1 border-b border-gray-100 flex-shrink-0">
            <div className="relative">
              <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={termoBusca}
                onChange={(e) => setTermoBusca(e.target.value)}
                placeholder={`Pesquisar em "${canalSelecionado.nome}"...`}
                className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-bravo-primary focus:border-bravo-primary"
              />
              {termoBusca && (
                <button
                  type="button"
                  onClick={() => setTermoBusca('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Limpar busca"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {topicos.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <p className="font-medium">Nenhum tópico ainda</p>
              <p className="text-sm mt-2">
                {podeCriarTopico ? 'Seja o primeiro a criar um tópico!' : 'Aguarde novos tópicos neste canal'}
              </p>
            </div>
          ) : topicosFiltrados.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="font-medium">Nenhum tópico encontrado para "{termoBusca}"</p>
              <p className="text-sm mt-2">Tente outro termo de busca.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topicosFiltrados.map((topico) => (
                <div
                  key={topico.id}
                  onClick={() => selecionarTopico(topico.id)}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {topico.fixado && (
                          <span className="text-yellow-500" title="Tópico fixado">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                            </svg>
                          </span>
                        )}
                        <h3 className="text-lg font-semibold text-gray-800 hover:text-bravo-primary">
                          {topico.titulo}
                        </h3>
                      </div>
                      <p className="text-gray-600 mt-2 line-clamp-2">{topico.mensagem}</p>
                      
                      {topico.anexos && topico.anexos.length > 0 && (
                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <span>{topico.anexos.length} {topico.anexos.length === 1 ? 'anexo' : 'anexos'}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <img
                            src={topico.autor_avatar || '/profilepicture.png'}
                            alt={topico.autor_nome}
                            className="w-5 h-5 rounded-full"
                          />
                          <span>{topico.autor_nome}</span>
                        </div>
                        <span>•</span>
                        <span>
                          {format(new Date(topico.data_criacao), 'dd/MM/yyyy HH:mm')}
                        </span>
                        {topico.total_comentarios !== undefined && topico.total_comentarios > 0 && (
                          <>
                            <span>•</span>
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                              <span>{topico.total_comentarios} {topico.total_comentarios === 1 ? 'comentário' : 'comentários'}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {(isAdmin || topico.autor_id === usuario?.id) && (
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={(e) => handleEditarTopico(topico.id, e)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Editar tópico"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => handleRemoverTopico(topico.id, e)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Remover tópico"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {modalCriarAberto && canalSelecionado && (
        <ModalCriarTopico
          canalId={canalSelecionado.id}
          canalNome={canalSelecionado.nome}
          onClose={() => setModalCriarAberto(false)}
        />
      )}

      {modalEditarAberto && topicoParaEditar && (
        <ModalEditarTopico
          topicoId={topicoParaEditar}
          onClose={() => {
            setModalEditarAberto(false);
            setTopicoParaEditar(null);
          }}
        />
      )}

      <ConfirmDialog
        open={modalConfirmacaoAberto}
        title="Remover Tópico"
        message="Tem certeza que deseja remover este tópico? Esta ação não pode ser desfeita."
        confirmText="Remover"
        onConfirm={confirmarRemocao}
        onCancel={() => setModalConfirmacaoAberto(false)}
      />

      {modalMembrosAberto && canalSelecionado && (
        <ModalMembrosCanal
          isOpen={modalMembrosAberto}
          onClose={() => setModalMembrosAberto(false)}
          canalNome={canalSelecionado.nome}
          departamentoId={canalSelecionado.departamento_id || null}
        />
      )}

      {modalGerenciarMembrosAberto && canalSelecionado && (
        <ModalEditarCanal
          canalId={canalSelecionado.id}
          onClose={() => setModalGerenciarMembrosAberto(false)}
        />
      )}
    </>
  );
};

export default AreaTopicos;
